'use client'

import type { FileUIPart } from 'ai'
import { useCallback, useRef, useState } from 'react'

import { useCreateAttachmentMutation } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'

/** assistant-api refuses a larger file; checking first spares the operator a round trip. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

/** assistant-api refuses a message carrying more files. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5

/** The file types assistant-api accepts, for the file picker. */
export const ACCEPTED_ATTACHMENT_TYPES = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.txt,.md,.markdown,.json,.xlsx'

export type PendingAttachment = {
  key: string
  filename: string
  status: 'uploading' | 'ready' | 'failed'
  /** Why a failed upload failed: over the size limit, past the per-message count, or refused or lost on the way. */
  failure?: 'tooLarge' | 'tooMany' | 'uploadFailed'
  /** The file part the next message carries, once the upload has finished. */
  part?: FileUIPart
}

/**
 * Files the operator attaches to their next message. Each is registered with assistant-api under the conversation
 * `conversationId` resolves to, which answers with a presigned URL the browser PUTs the bytes to; the message then
 * references the upload by a file part. `conversationId` is asked for at upload time, so the empty state can
 * create its conversation when the first file is attached.
 */
export default function useAttachmentUploads(conversationId: () => Promise<string>) {
  const [createAttachment] = useCreateAttachmentMutation()
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const nextKey = useRef(0)

  const update = useCallback((key: string, change: Partial<PendingAttachment>) => {
    setAttachments((current) => current.map((item) => (item.key === key ? { ...item, ...change } : item)))
  }, [])

  const upload = useCallback(
    async (key: string, file: File) => {
      try {
        const created = await createAttachment({
          conversationId: await conversationId(),
          attachmentCreateParams: { filename: file.name, contentType: file.type || null, sizeBytes: file.size }
        }).unwrap()
        const response = await fetch(created.uploadUrl, { method: 'PUT', headers: created.uploadHeaders, body: file })
        if (!response.ok) throw new Error(`The attachment upload answered ${response.status}`)
        update(key, {
          status: 'ready',
          part: { type: 'file', mediaType: created.contentType, filename: created.filename, url: created.url }
        })
      } catch (error) {
        logger.error('Failed to upload an assistant attachment', error)
        update(key, { status: 'failed', failure: 'uploadFailed' })
      }
    },
    [conversationId, createAttachment, update]
  )

  const add = useCallback(
    (files: File[]) => {
      let slots = MAX_ATTACHMENTS_PER_MESSAGE - attachments.filter((item) => item.status !== 'failed').length
      const added = files.map((file): PendingAttachment => {
        nextKey.current += 1
        const key = `attachment-${nextKey.current}`
        if (file.size > MAX_ATTACHMENT_BYTES) return { key, filename: file.name, status: 'failed', failure: 'tooLarge' }
        if (slots <= 0) return { key, filename: file.name, status: 'failed', failure: 'tooMany' }
        slots -= 1
        void upload(key, file)
        return { key, filename: file.name, status: 'uploading' }
      })
      setAttachments((current) => [...current, ...added])
    },
    [attachments, upload]
  )

  const remove = useCallback((key: string) => {
    setAttachments((current) => current.filter((item) => item.key !== key))
  }, [])

  const clear = useCallback(() => setAttachments([]), [])

  const files = attachments.flatMap((item) => (item.part ? [item.part] : []))
  const isUploading = attachments.some((item) => item.status === 'uploading')
  return { attachments, files, isUploading, add, remove, clear }
}
